/**
 * Licensed stock photography, used only where the client's own photo library has
 * no high-resolution image that matches the service 100%.
 *
 * Sources: Unsplash (Unsplash License) and Pexels (Pexels License) — both permit
 * commercial use without attribution. Every image was visually verified against
 * the service it represents.
 *
 * These are now **self-hosted** (converted to WebP, imported through Vite so they
 * get content-hashed, immutably-cached /assets/* URLs) instead of hot-linked from
 * images.pexels.com / images.unsplash.com. Hot-linking pulled a third image
 * origin into every page and — in Pexels' case — set third-party Cloudflare
 * cookies that failed the Lighthouse "avoid third-party cookies" best-practice.
 *
 * Replace any entry with a client photo as soon as a matching one exists; drop a
 * new WebP into src/assets/stock/ and repoint the import.
 */

import residentialCard from "@/assets/stock/residential-cleaning-card.webp";
import residentialHero from "@/assets/stock/residential-cleaning-hero.webp";
import commercialCard from "@/assets/stock/commercial-cleaning-card.webp";
import commercialHero from "@/assets/stock/commercial-cleaning-hero.webp";
import interiorCard from "@/assets/stock/interior-painting-card.webp";
import interiorHero from "@/assets/stock/interior-painting-hero.webp";
import kitchenCard from "@/assets/stock/kitchen-cabinets-card.webp";
import kitchenHero from "@/assets/stock/kitchen-cabinets-hero.webp";
import houseCard from "@/assets/stock/house-exterior-card.webp";
import houseHero from "@/assets/stock/house-exterior-hero.webp";

type StockPhoto = { readonly card: string; readonly hero: string };

export const stock = {
  /** Gloved hands wiping a home surface with spray and cloth. Hands only. */
  residentialCleaning: { card: residentialCard, hero: residentialHero },
  /** Mop bucket and wet-floor sign in a commercial lobby. No people. */
  commercialCleaning: { card: commercialCard, hero: commercialHero },
  /** Roller laying fresh paint on an interior wall. Hand only, no face. */
  interiorPainting: { card: interiorCard, hero: interiorHero },
  /** Kitchen with freshly finished white shaker cabinetry. No people. */
  kitchenCabinets: { card: kitchenCard, hero: kitchenHero },
  /** White-sided house with wraparound porch and manicured lawn. No people. */
  houseExterior: { card: houseCard, hero: houseHero },
} as const satisfies Record<string, StockPhoto>;

/** Card crop — matches the 4:3 service card frame. */
export const stockCard = (p: StockPhoto) => p.card;

/** Wide crop for full-bleed section heroes. */
export const stockHero = (p: StockPhoto) => p.hero;
